import './App.css';
import React from "react";
import {useState, useEffect, useLayoutEffect} from 'react';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from '@chakra-ui/react'; // Import necessary components
import { Box, Flex, Spacer, Center, Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Drawer,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  HStack, ButtonGroup, Button, SimpleGrid, Container, Image, Text, Icon, Avatar, IconButton, useToast} from "@chakra-ui/react"
import { FaInstagram, FaUser } from "react-icons/fa";
import {EmailIcon, HamburgerIcon} from '@chakra-ui/icons';
import BioModal from './components/dynamic/BioModal';
import Dashboard from './components/dynamic/Dashboard';
import OrderStatus from './components/dynamic/OrderStatus';
import SLAfterPayment from './components/static_pages/SLAfterPayment';
import CheckoutForm from './components/dynamic/CheckoutForm';
import Home from './components/static_pages/Home';
import Editor from './components/dynamic/Editor';
import Auth from './components/static_pages/Auth';
import About from "./components/static_pages/About";
import Blog from './components/static_pages/Blog';
import AboutPantLength from './components/static_pages/blog_entries/About_pant_length';
import PrepareWardrobeForSpring from './components/static_pages/blog_entries/Prepare_wardrobe_for_spring';
import HowToCreateAnImpactfulStyle from './components/static_pages/blog_entries/How_to_create_an_impactful_style';
import ChristasParty from './components/static_pages/blog_entries/Christmas_party';
import Pricing from './components/static_pages/Pricing';
import Welcome from './components/static_pages/Welcome';
import FAQ from './components/static_pages/FAQ';
import NotFound from './components/static_pages/NotFound';
import MyRequests from './components/dynamic/MyRequests';
import Privacy from './components/static_pages/Privacy';
import TermsAndConditions from './components/static_pages/TermsAndConditions';
// import Testimonials from './components/static_pages/Testimonials';
import RequireAuth from './components/utils/RequireAuth';
import Consent from './components/dynamic/Consent'; 
import ScrollToTop from  './components/utils/ScrollToTop';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation
} from "react-router-dom";
import { supabase } from './components/utils/supabaseClient';
import {
  captureRecoveryFromUrl,
  clearPasswordRecovery,
  isPasswordRecoveryActive,
  isUpdatePasswordAuthRoute,
  markPasswordRecovery,
} from './components/utils/passwordRecovery';
import HowItWorks from './components/static_pages/HowItWorks';
import HowIStyle from './components/static_pages/HowIStyle';
import Inquiries from './components/static_pages/Inquiries';
import ShoppingLists from './components/dynamic/ShoppingLists';

const AUTH_PATHS = new Set(['/auth', '/login', '/signup']);

/** Must render under `<Router>` — hooks need router context */
function SupabaseAuthNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Capture recovery tokens before Supabase client strips the hash from the URL.
  useLayoutEffect(() => {
    if (!captureRecoveryFromUrl()) {
      return;
    }
    if (location.pathname !== '/auth') {
      navigate(
        {
          pathname: '/auth',
          search: '?view=update_password',
          hash: location.hash,
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.hash, navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecovery();
        if (window.location.pathname !== '/auth') {
          navigate('/auth?view=update_password', { replace: true });
        }
        return;
      }

      if (event === 'USER_UPDATED') {
        const wasPasswordReset =
          isPasswordRecoveryActive() || isUpdatePasswordAuthRoute();
        clearPasswordRecovery();
        if (wasPasswordReset) {
          toast({
            title: 'Slaptažodis pakeistas sėkmingai',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          navigate('/', { replace: true });
        }
      }

      if (event === 'SIGNED_OUT') {
        clearPasswordRecovery();
        navigate('/auth?view=sign_in');
        return;
      }

      if (event === 'SIGNED_IN') {
        if (isPasswordRecoveryActive()) {
          return;
        }
        // Email/password sign-in may not leave /auth; give Auth UI redirectTo time first.
        window.setTimeout(() => {
          if (
            !AUTH_PATHS.has(window.location.pathname) ||
            isPasswordRecoveryActive() ||
            isUpdatePasswordAuthRoute()
          ) {
            return;
          }
          const view = new URLSearchParams(window.location.search).get('view');
          navigate(view === 'sign_up' ? '/sveiki' : '/');
        }, 300);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  // Signed-in user opened /auth manually — send home (skip during password recovery).
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (
        !cancelled &&
        session?.access_token &&
        AUTH_PATHS.has(location.pathname) &&
        !isPasswordRecoveryActive() &&
        !isUpdatePasswordAuthRoute()
      ) {
        navigate('/');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  return null;
}

function App() {
  const [isMediumScreen] = useMediaQuery("(min-width: 80em)"); // Medium screen and above
  const [isSmallScreen] = useMediaQuery("(max-width: 30em)"); // Small screen (less than 30em)
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure()
  const [isEditor, setIsEditor] = useState(false);
  const [supabaseSession, setSupabaseSession] = useState(null);

  const supabaseAccessToken = supabaseSession?.access_token;
  const isSupabaseLoggedIn = Boolean(supabaseAccessToken);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.log('Error logging out:', error.message);
  };

  useEffect(() => {
    const applySession = (session) => {
      setSupabaseSession(session);
      setIsEditor(session?.user?.app_metadata?.role === 'editor');
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        applySession(session);
      }
    );
    return () => subscription.unsubscribe();
  }, []);


  let menu = <></>;
  if (!isSupabaseLoggedIn) {
  menu =
  <Box
    display='flex'
    alignItems='center'
    justifyContent='center'
    width='100%'
    minHeight='68px'>
  <ButtonGroup gap='2' p='2'>
    <Button 
      // colorscheme='white' 
      color='black.500'
      borderColor='black.500'
      borderWidth='2px'
      variant='outline'
      sx={{
        '&:hover': {
          bg: "rgba(251, 143, 255, 0.4)"
        },
      }} 
    ><Link to="/auth?view=sign_up">Registruotis</Link></Button>
    <Button 
          mr='6' 
          colorscheme='white' 
          color='black.500'
          borderColor='black.500'
          borderWidth='2px'
          variant='outline'
          sx={{
            '&:hover': {
              bg: "rgba(251, 143, 255, 0.4)"
            },
          }}
    ><Link to="/auth?view=sign_in">Prisijungti</Link></Button>
  </ButtonGroup>
  </Box>;
  } else {
    menu = <>
    <Box
    display='flex'
    alignItems='center'
    justifyContent='center'
    width='100%'>
    {isEditor && !isSmallScreen? (<Button 
          mr='6' 
          colorscheme='white' 
          color='black.500'
          borderColor='black.500'
          borderWidth='2px'
          variant='outline'
          sx={{
            '&:hover': {
              bg: "rgba(251, 143, 255, 0.4)"
            },
          }}
    ><Link to="/uzklausos">Atsakyti užklausas</Link></Button>) : null }
    {!isSmallScreen ? ( 
          <Button 
          mr='6' 
          colorscheme='white' 
          color='black.500'
          borderColor='black.500'
          borderWidth='2px'
          variant='outline'
          sx={{
            '&:hover': {
              bg: "rgba(251, 143, 255, 0.4)"
            },
          }}
        >
          <Link to="/mano-uzklausos">Mano užklausos</Link>
        </Button>
    ) : null}
    <Menu isLazy>
    <MenuButton mr='2' colorscheme='pink'>
      <Avatar
        size='lg'
        name={
          supabaseSession?.user?.user_metadata?.full_name ||
          supabaseSession?.user?.email ||
          ''
        }
        src={
          supabaseSession?.user?.user_metadata?.avatar_url ||
          supabaseSession?.user?.user_metadata?.picture
        }
        referrerPolicy="no-referrer"
      >
      </Avatar>
    </MenuButton>
    <MenuList>
      {/* <MenuGroup title='Paskyra'> */}
        <MenuItem onClick={onModalOpen}>
          Apie mane
        </MenuItem>
        <Link to="/auth?view=update_password"><MenuItem>Keisti slaptažodį</MenuItem></Link>
      {isSmallScreen ? (
      <>
        <Link to="/mano-uzklausos"><MenuItem><Text fontWeight="bold">Mano užklausos</Text></MenuItem></Link>
      </>
      ) : null}
      {isSmallScreen && isEditor? (
      <>
        <Link to="/uzklausos"><MenuItem><Text fontWeight="bold">Atsakyti užklausas</Text></MenuItem></Link>
      </>
      ) : null}
        <MenuItem onClick={handleLogout}>Atsijungti</MenuItem>
    </MenuList>
  </Menu>  
  <BioModal onModalOpen={onModalOpen} onModalClose={onModalClose} isOpen={isModalOpen}/>
  </Box>
  </>
  }

  return (
    <>
    <Helmet>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#000000" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
      <meta name="description" content="Personalizuotos stiliaus konsultacijos kiekvienai, kuri nori jaustis savimi" data-rh="true"/>
      <title>Ką rengtis pagal Viliją Rušinskaitę</title>
    </Helmet>
    <Flex direction="column" minHeight="100vh"> {/* Flex container for full height */}
    <Box flex="1"> {/* This will take up the remaining space */}
    <Consent />
    <Router>
    <SupabaseAuthNavigation />
    <ScrollToTop />
    <div>
    <Flex 
    position='fixed' // Fix the element to the top
    top='0' // Position it at the top
    left='0' // Align it to the left
    right='0' // Stretch it to the right
    zIndex='1000' // Ensure it appears above other elements
    minWidth='max-content' 
    alignItems='center' 
    justifyContent='space-between'
    gap='2' 
    borderBottom='1px' 
    borderColor='black'
    backgroundColor='white'
    minHeight='80px'
     >
    {!isMediumScreen ? (
          <IconButton 
          icon={<HamburgerIcon />} 
          aria-label="Menu" 
          variant="outline" 
          colorscheme='white' 
          color='black.500'
          borderColor='black.500'
          borderWidth='2px'
          onClick={onDrawerOpen}
          sx={{ 
            ml: '12px',
            mr: '12px',
            '&:hover': {
              bg: "rgba(251, 143, 255, 0.4)"
            },
            fontSize: '24px', // Adjust font size
            padding: '16px', // Adjust padding
            minWidth: '48px', // Set a minimum width
            minHeight: '48px', // Set a minimum height
          }}  />
    ) : null}
      <Drawer placement={"left"} onClose={onDrawerClose} isOpen={isDrawerOpen}>
        <DrawerOverlay />
        <DrawerContent>
        <DrawerCloseButton borderBottomWidth='0px' fontSize="18px" pt='4'/>
        <DrawerHeader borderBottomWidth='0px' pt='6'></DrawerHeader>
          <Link onClick={onDrawerClose} to="/apie"><DrawerHeader borderBottomWidth='1px'>Apie mane</DrawerHeader></Link>
          <Link onClick={onDrawerClose} to="/kaina"><DrawerHeader borderBottomWidth='1px'>Kaina</DrawerHeader></Link>
          <Link onClick={onDrawerClose} to="/tinklarastis"><DrawerHeader borderBottomWidth='1px'>Tinklaraštis</DrawerHeader></Link>
          <Link onClick={onDrawerClose} to="/susisiekime"><DrawerHeader borderBottomWidth='1px'>Susisiekime</DrawerHeader></Link>
          {/* <DrawerHeader borderBottomWidth='1px'><Link onClick={onDrawerClose} to="/atsiliepimai">Klientų atsiliepimai</Link></DrawerHeader> */}
        </DrawerContent>
      </Drawer>
      <Box sx={{
        p:'2',
        overflow:'hidden',
        maxW:['45vw', '15vw', '15vw', '15vw',]
      }}>
        <Link to="/">
        <Image src="ka_rengtis_logo_1.png" alt="Ką rengtis?" />
        </Link>
      </Box>
      {isMediumScreen ? (
        <>
      <Box sx={{
        p:'2',
        fontWeight:'bold',
        fontSize: ['12px', '12px', '18px', '24px']
      }}
      ><Link to="/apie">Apie mane</Link></Box>
      <Box sx={{
        p:'2',
        fontWeight:'bold',
        fontSize: ['12px', '12px', '18px', '24px']
      }}
      ><Link to="/kaina">Kaina</Link></Box>
      <Box sx={{
        p:'2',
        fontWeight:'bold',
        fontSize: ['12px', '12px', '18px', '24px']
      }}
      ><Link to="/tinklarastis">Tinklaraštis</Link></Box>
      <Box sx={{
        p:'2',
        fontWeight:'bold',
        fontSize: ['12px', '12px', '18px', '24px']
      }}><Link to="/susisiekime">Susisiekime</Link></Box>
      {/* <Box sx={{
        p:'2',
        fontWeight:'bold',
        fontSize: ['12px', '12px', '18px', '24px']
      }}
      ><Link to="/atsiliepimai">Klientų atsiliepimai</Link></Box> */}
      </>
      ) : null}
      {!isSmallScreen ? (<Spacer />) : null}
      <Box p='2'>
      <Center>
      {isSmallScreen && !isSupabaseLoggedIn ? ( 
        <>
              <Menu isLazy>
              <MenuButton mr='0' colorscheme='pink'>
                <Avatar size='lg' color='black' bg='white' referrerPolicy="no-referrer" icon={<FaUser color="black"/>}>
                </Avatar>
              </MenuButton>
              <MenuList>
              <Link to="/auth?view=sign_up">
                  <MenuItem>
                    Registruotis  
                  </MenuItem>
              </Link> 
              <Link to="/auth?view=sign_in"><MenuItem>Prisijungti</MenuItem></Link>
              </MenuList>
            </Menu> 
            </>
      ) : menu}
      </Center>   
      </Box>
    </Flex>
    <Box 
    sx = {{ minH: ['82.7vh', '82.7vh', '82.7vh', '82.7vh']
    }}
     bg="rgba(251, 143, 255, 0.2)"
     marginTop={"80px"}
      >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/apie" element={<About />} />
        <Route path="/shopping-listai" element={<ShoppingLists />} />
        <Route path="/apmokejimas-shopping-listai" element={<SLAfterPayment />} />
        <Route path="/tinklarastis" element={<Blog />} />
        <Route path="/tinklarastis/apie_kelniu_ilgi" element={<AboutPantLength />} />
        <Route path="/tinklarastis/spintos_paruosimas_pavasariui" element={<PrepareWardrobeForSpring />} />
        <Route path="/tinklarastis/kaip_susikurti_poveiki_daranti_stiliu" element={<HowToCreateAnImpactfulStyle />} />
        <Route path="/tinklarastis/kaledinis_vakarelis" element={<ChristasParty />} />
        <Route path="/kaina" element={<Pricing />} />
        <Route path="/kaip-tai-veikia" element={<HowItWorks />} />
        <Route path="/kaip-as-stilizuoju" element={<HowIStyle />} />
        <Route path="/susisiekime" element={<Inquiries />} />
        {/* <Route path="/atsiliepimai" element={<Testimonials />} /> */}
        <Route path="/duk" element={<FAQ />} />
        <Route path="/salygos-ir-nuostatos" element={<TermsAndConditions />} />
        <Route path="/privatumo-politika" element={<Privacy />} />
        <Route path="/auth" element={<Auth />} />
        <Route 
          path="/sveiki" 
          element={
            <RequireAuth>
              <Welcome />
            </RequireAuth>
          } 
        />
        <Route 
          path="/apmokejimas" 
          element={
            <RequireAuth>
              <CheckoutForm />
            </RequireAuth>
          } 
        />
        <Route 
          path="/uzsakymo-busena" 
          element={
            <RequireAuth>
              <OrderStatus />
            </RequireAuth>
          } 
        />
        <Route
            path="/uzklausa"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
        <Route
            path="/mano-uzklausos"
            element={
              <RequireAuth>
                <MyRequests />
              </RequireAuth>
            }
          />
        <Route
            path="/uzklausos"
            element={
              <RequireAuth>
                <Editor />
              </RequireAuth>
            }
          />
        <Route path='*' element={<NotFound />}/>
      </Routes>
      </Box>
      <SimpleGrid 
        columns={1} // Keep as 1 for a single column layout
        spacing={0} 
        borderTop='1px' 
        borderColor='black'
        as="footer"
      >
        <Container>
          <HStack spacing='48px' justifyContent='center'> {/* Center align the first row */}
            <Box p='2' fontWeight='bold'
            sx={{ 
              fontSize: ['sm', 'lg', 'lg', 'lg'], }}
            ><Link to="/salygos-ir-nuostatos">Sąlygos ir nuostatos</Link></Box>
            <Box p='2' fontWeight='bold' 
            sx={{ 
              fontSize: ['sm', 'lg', 'lg', 'lg'], }}><Link to="/privatumo-politika">Privatumo politika</Link></Box>
            <Box p='2' fontWeight='bold'
            sx={{ 
              fontSize: ['sm', 'lg', 'lg', 'lg'], }}><Link to="/duk">D.U.K.</Link></Box>
          </HStack>
        </Container>
        <Container>
          <HStack spacing='24px' justifyContent='center'> {/* Center align the second row */}
            <HStack  sl='2' sr='2' sb='2' pl='2' pr='2' pb='2'> {/* Use HStack for horizontal alignment */}
              <EmailIcon/><Text fontSize='ml' fontWeight='bold'
              sx={{ 
                fontSize: ['sm', 'lg', 'lg', 'lg'], }}> vilija@karengtis.lt</Text>
            </HStack>
              <HStack sl='2' sr='2' sb='2' pl='2' pr='2' pb='2'> {/* Use HStack for horizontal alignment */}
                <Icon as={FaInstagram} boxSize={5} fontWeight='semibold'/>
                <Link to="https://www.instagram.com/karengtis.lt/">
                  <Text fontSize='ml' fontWeight='bold'
                  sx={{ 
                    fontSize: ['sm', 'lg', 'lg', 'lg'], }}> @karengtis.lt</Text>
                </Link>
              </HStack>
          </HStack>
        </Container>
      </SimpleGrid>
    </div>
  </Router>
  </Box>
  </Flex>
  </>
  );
}

export default App;
